import React from "react";
import { useParams } from "react-router-dom";

import DetailClass from "../../../templates/teacher/DetailClass";
import {
  Context,
  fetchClasses,
  fetchGroups,
  fetchStudents,
  nameClosed,
  nameOpened,
  publishTeachingMaterial,
  unpublishTeachingMaterial,
  updateGroup,
  deleteGroup,
  answerOpened,
  answerClosed,
  fetchTeachingMaterialsByClass,
  publishLesson,
  unpublishLesson,
  createGroup,
  clearSuccessMessage,
  CREATE_GROUP_SUCCESS_MESSAGE,
  CREATE_GROUP_FAILURE_MESSAGE,
  clearTeachingMaterialsByClass,
} from "../../../teacherStore";
import { IGroup } from "../../../state";
import useWaitApiCall from "../../../hooks/useWaitApiCall";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const { id } = useParams<{ id: string }>();
  React.useEffect(() => {
    dispatch(fetchGroups([Number(id)]));
    dispatch(fetchClasses());
    dispatch(fetchTeachingMaterialsByClass(Number(id)));
    return () => dispatch(clearTeachingMaterialsByClass());
  }, [dispatch, id]);

  React.useEffect(() => {
    if (state.survey_enabled) {
      dispatch(fetchStudents([Number(id)]));
    }
  }, [dispatch, id, state.survey_enabled]);

  React.useEffect(() => {
    if (state.success_message === CREATE_GROUP_SUCCESS_MESSAGE) {
      dispatch(fetchGroups([Number(id)]));
      dispatch(clearSuccessMessage());
    }
  }, [dispatch, state.success_message, id]);

  const publishFunc = React.useCallback(
    (teaching_material_id: number) => {
      dispatch(peep(publishTeachingMaterial(Number(id), teaching_material_id)));
    },
    [dispatch, id, peep],
  );
  const unPublishFunc = React.useCallback(
    (teaching_material_id: number) => {
      dispatch(
        peep(unpublishTeachingMaterial(Number(id), teaching_material_id)),
      );
    },
    [dispatch, id, peep],
  );
  const publishLessonFunc = React.useCallback(
    (lesson_id: number, class_id: number) => {
      dispatch(peep(publishLesson([class_id], lesson_id)));
    },
    [dispatch, peep],
  );
  const unPublishLessonFunc = React.useCallback(
    (lesson_id: number, class_id: number) => {
      dispatch(peep(unpublishLesson(class_id, lesson_id)));
    },
    [dispatch, peep],
  );
  const changeNameOpenClose = React.useCallback(
    (issue_id: number, opened: boolean) => {
      if (opened) {
        dispatch(peep(nameOpened(issue_id, Number(id))));
      } else {
        dispatch(peep(nameClosed(issue_id, Number(id))));
      }
    },
    [dispatch, id, peep],
  );
  const changeAnswerOpenClose = React.useCallback(
    (issue_id: number, opened: boolean) => {
      if (opened) {
        dispatch(peep(answerOpened(issue_id, Number(id))));
      } else {
        dispatch(peep(answerClosed(issue_id, Number(id))));
      }
    },
    [dispatch, id, peep],
  );
  const createGroupFunc = React.useCallback(
    (classID: number, name: string) => {
      dispatch(peep(createGroup(classID, name)));
    },
    [dispatch, peep],
  );
  const updateGroupFunc = React.useCallback(
    (group: IGroup) => {
      dispatch(peep(updateGroup(group)));
    },
    [dispatch, peep],
  );
  const deleteGroupFunc = React.useCallback(
    (groupId: number) => dispatch(peep(deleteGroup(groupId))),
    [dispatch, peep],
  );

  const currentClass = state.classes.find(cls => cls.id === Number(id));

  const requestCreateGroupDone =
    state.success_message === CREATE_GROUP_SUCCESS_MESSAGE ||
    state.error === CREATE_GROUP_FAILURE_MESSAGE;

  return (
    <DetailClass
      enabledSurvey={state.survey_enabled}
      currentClass={currentClass!}
      groups={state.groups}
      students={state.students}
      teaching_materials={
        state.teaching_materials_by_class.length > 0
          ? state.teaching_materials_by_class
          : []
      }
      requestCreateGroupDone={requestCreateGroupDone}
      onCreateGroup={createGroupFunc}
      changeNameOpenClose={changeNameOpenClose}
      changeAnswerOpenClose={changeAnswerOpenClose}
      onPublishMaterial={publishFunc}
      onUnPublishMaterial={unPublishFunc}
      onPublishLesson={publishLessonFunc}
      onUnPublishLesson={unPublishLessonFunc}
      onUpdateGroup={updateGroupFunc}
      onDeleteGroup={deleteGroupFunc}
      calling={calling}
    />
  );
};
